
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Request, User, UserRole, RequestStatus, Question, Interest, Urgency } from './types';

interface AppState {
  currentUser: User | null;
  requests: Request[];
  questions: Question[];
  interests: Interest[];
  setCurrentUser: (user: User | null) => void;
  addRequest: (request: Omit<Request, 'id' | 'createdAt' | 'status'>) => Request;
  updateRequestStatus: (id: string, status: RequestStatus, artisanId?: string) => void;
  addQuestion: (requestId: string, artisanId: string, artisanName: string, text: string) => void;
  answerQuestion: (questionId: string, answer: string) => void;
  expressInterest: (requestId: string, artisanId: string, artisanName: string, type: 'interested' | 'accept') => void;
  completeJob: (requestId: string, rating: number, review: string) => void;
}

const AppContext = createContext<AppState | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [requests, setRequests] = useState<Request[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [interests, setInterests] = useState<Interest[]>([]);

  // Initialize with dummy data
  useEffect(() => {
    const initialRequests: Request[] = [
      {
        id: '1',
        requesterId: 'user1',
        requesterName: 'Sarah Jenkins',
        text: 'I ripped the zipper on my favorite vintage dress and need it fixed for a wedding tomorrow!',
        category: 'Tailor',
        urgency: Urgency.ASAP,
        status: RequestStatus.OPEN,
        createdAt: Date.now() - 3600000,
        estimatedPrice: '$20 - $45',
        location: { lat: 37.7749, lng: -122.4194, address: 'Mission District, SF' }
      },
      {
        id: '2',
        requesterId: 'user2',
        requesterName: 'Mike Ross',
        text: 'The lock on my front door is sticking. I can barely get my key in.',
        category: 'Locksmith',
        urgency: Urgency.TODAY,
        status: RequestStatus.OPEN,
        createdAt: Date.now() - 7200000,
        estimatedPrice: '$80 - $120',
        location: { lat: 37.7833, lng: -122.4167, address: 'Tenderloin, SF' }
      }
    ];
    setRequests(initialRequests);
  }, []);

  const addRequest = useCallback((req: Omit<Request, 'id' | 'createdAt' | 'status'>) => {
    const newRequest: Request = {
      ...req,
      id: Math.random().toString(36).substr(2, 9),
      createdAt: Date.now(),
      status: RequestStatus.POSTED
    };
    setRequests(prev => [newRequest, ...prev]);
    return newRequest;
  }, []);

  const updateRequestStatus = useCallback((id: string, status: RequestStatus, artisanId?: string) => {
    setRequests(prev => prev.map(r => 
      r.id === id ? { ...r, status, assignedArtisanId: artisanId || r.assignedArtisanId } : r
    ));
  }, []);

  const addQuestion = useCallback((requestId: string, artisanId: string, artisanName: string, text: string) => {
    const newQuestion: Question = {
      id: Math.random().toString(36).substr(2, 9),
      requestId,
      artisanId,
      artisanName,
      question: text,
      createdAt: Date.now()
    };
    setQuestions(prev => [...prev, newQuestion]);
    updateRequestStatus(requestId, RequestStatus.CLARIFYING);
  }, [updateRequestStatus]);

  const answerQuestion = useCallback((questionId: string, answer: string) => {
    setQuestions(prev => prev.map(q => q.id === questionId ? { ...q, answer } : q));
  }, []);

  const expressInterest = useCallback((requestId: string, artisanId: string, artisanName: string, type: 'interested' | 'accept') => {
    setInterests(prev => {
      // Don't add duplicate interest
      if (prev.find(i => i.requestId === requestId && i.artisanId === artisanId)) return prev;
      
      const newInterest: Interest = {
        id: Math.random().toString(36).substr(2, 9),
        requestId,
        artisanId,
        artisanName,
        type,
        createdAt: Date.now()
      };
      return [...prev, newInterest];
    });
    
    if (type === 'accept') {
      // In a real app, 'accept' might automatically notify the user
    }
  }, []);

  const completeJob = useCallback((requestId: string, rating: number, review: string) => {
    updateRequestStatus(requestId, RequestStatus.COMPLETED);
    // In real app, we would save the review and rating to artisan's profile
    console.log(`Job ${requestId} completed with ${rating} stars: ${review}`);
  }, [updateRequestStatus]);

  return (
    <AppContext.Provider value={{
      currentUser, requests, questions, interests,
      setCurrentUser, addRequest, updateRequestStatus,
      addQuestion, answerQuestion, expressInterest, completeJob
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
};
